/* eslint-disable react-hooks/rules-of-hooks */

import { SvgIconComponent } from "@material-ui/icons";
import { ProcessedListingData, GenericPropertyDetails } from "../../../@types/sharedSchema";
import {
  Spa,
  Home,
  Fence,
  AcUnit,
  Balcony,
  Shield,
  Security,
  Elevator,
  DoorBack,
  Accessible,
  Foundation,
  FormatPaint,
  LocalParking
} from "@material-ui/icons";
import { grey } from "@material-ui/core/colors";
import { useTranslation } from "react-i18next";
import { Box, Chip, Stack, Typography } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import Label from "components/Label";
import useLocales from "hooks/useLocales";
import { PROTECTED_SPACE_GROUP_HINT, PROTECTED_SPACE_GROUP_LABEL, PROTECTED_SPACE_KEYS } from "utils/contacts/amenityLabels";

type Details = {
  id: keyof GenericPropertyDetails;
  title: string;
  value: boolean;
  Icon?: SvgIconComponent;
  usingRTL?: boolean;
  formattedValue?: string;
};

export function getListingBooleanPropertiesDetails(
  data: Partial<GenericPropertyDetails>,
  { filterFalsyValue }: { filterFalsyValue?: boolean } = {}
): Details[] {
  const { t } = useTranslation();

  if (!data) {
    return;
  }

  const boolToEmoji = (bool: boolean) => (bool ? "✔️" : "❌");

  let propertyDetails: Details[] = [
    {
      id: "hasElevator",
      title: t("listingsPage.tableColumns.hasElevator"),
      value: data.hasElevator,
      formattedValue: boolToEmoji(data.hasElevator),
      Icon: Elevator
    },
    {
      id: "hasGarden",
      title: t("listingsPage.tableColumns.hasGarden"),
      value: data.hasGarden,
      formattedValue: boolToEmoji(data.hasGarden),
      Icon: Spa
    },
    {
      id: "hasParking",
      title: t("listingsPage.tableColumns.hasParking"),
      value: data.hasParking,
      formattedValue: boolToEmoji(data.hasParking),
      Icon: LocalParking
    },
    {
      id: "hasStorage",
      title: t("listingsPage.tableColumns.hasStorage"),
      value: data.hasStorage,
      formattedValue: boolToEmoji(data.hasStorage),
      Icon: DoorBack
    },
    {
      id: "hasTerrace",
      title: t("listingsPage.tableColumns.hasTerrace"),
      value: data.hasTerrace,
      formattedValue: boolToEmoji(data.hasTerrace),
      Icon: Balcony
    },
    {
      id: "hasBars",
      title: t("listingsPage.tableColumns.hasBars"),
      value: data.hasBars,
      formattedValue: boolToEmoji(data.hasBars),
      Icon: Fence
    },
    {
      id: "hasHousingUnit",
      title: t("listingsPage.tableColumns.hasHousingUnit"),
      value: data.hasHousingUnit,
      formattedValue: boolToEmoji(data.hasHousingUnit),
      Icon: Home
    },
    {
      id: "hasProtectedSpace",
      title: t("listingsPage.tableColumns.hasProtectedSpace"),
      value: data.hasProtectedSpace,
      formattedValue: boolToEmoji(data.hasProtectedSpace),
      Icon: Security
    },
    {
      id: "hasMamak",
      title: t("listingsPage.tableColumns.hasMamak"),
      value: data.hasMamak,
      formattedValue: boolToEmoji(data.hasMamak),
      Icon: Shield
    },
    {
      id: "hasMiklat",
      title: t("listingsPage.tableColumns.hasMiklat"),
      value: data.hasMiklat,
      formattedValue: boolToEmoji(data.hasMiklat),
      Icon: Foundation
    },
    {
      id: "hasAirConditioning",
      title: t("listingsPage.tableColumns.hasAirConditioning"),
      value: data.hasAirConditioning,
      formattedValue: boolToEmoji(data.hasAirConditioning),
      Icon: AcUnit
    },
    {
      id: "hasHandicappedAccess",
      title: t("listingsPage.tableColumns.accessibleForDisabled"),
      value: data.hasHandicappedAccess,
      formattedValue: boolToEmoji(data.hasHandicappedAccess),
      Icon: Accessible
    },
    {
      id: "isRenovated",
      title: t("listingsPage.tableColumns.isRenovated"),
      value: data.isRenovated,
      formattedValue: boolToEmoji(data.isRenovated),
      Icon: FormatPaint
    }
  ];

  if (filterFalsyValue) {
    propertyDetails = propertyDetails.filter(({ value }) => value);
  }

  return propertyDetails;
}

type ListingBooleanPropertiesDetailsBagProps = {
  data: Partial<GenericPropertyDetails>;
  loading?: boolean;
  contact?: boolean;
  filterFalsyValue?: boolean;
  onChangePropertyDetails?: (name: keyof GenericPropertyDetails) => () => void;
  hideProperties?: string[];
};

export function ListingBooleanPropertiesDetailsBag({
  data,
  contact,
  loading,
  filterFalsyValue,
  onChangePropertyDetails,
  hideProperties = []
}: ListingBooleanPropertiesDetailsBagProps): JSX.Element {
  const { t } = useTranslation();

  let propertyDetails = getListingBooleanPropertiesDetails(data, { filterFalsyValue });
  propertyDetails = propertyDetails?.filter(({ id }) => !hideProperties.includes(id));

  if (!propertyDetails?.length) return null;

  const isInUpdateMode = Boolean(onChangePropertyDetails);

  const chipOf = ({ id, title, value, usingRTL, formattedValue, Icon }: typeof propertyDetails[number]): JSX.Element => (
    <Chip
      variant="outlined"
      label={
        <Stack direction="row" sx={{ color: value ? grey[600] : grey[400] }} gap={0.5}>
          <Typography
            style={{ direction: usingRTL ? "rtl" : "ltr", opacity: 0.7 }}
            variant="subtitle1"
            color="primary.main"
          >
            {formattedValue}
          </Typography>
          <Typography variant="subtitle1">{title}</Typography>
          {Icon && <Icon />}
        </Stack>
      }
      disabled={loading}
      clickable={isInUpdateMode}
      onClick={onChangePropertyDetails?.(id)}
      key={id}
    />
  );

  // On a CONTACT the מרחב מוגן chips are one requirement with three ways of being satisfied, so
  // they are boxed together and say so. On a LISTING they are three independent facts about the
  // building and stay in the flat row. See `PROTECTED_SPACE_KEYS` — the server ORs the contact
  // side (`PROTECTED_SPACE_GROUP`), and this box is what makes that visible before a realtor
  // ticks all three and wonders why the התאמת נכס page went empty (reported 2026-09-22).
  const shelter = contact ? propertyDetails.filter((detail) => PROTECTED_SPACE_KEYS.includes(detail.id)) : [];
  const rest = contact ? propertyDetails.filter((detail) => !PROTECTED_SPACE_KEYS.includes(detail.id)) : propertyDetails;
  const shelterActive = shelter.some((detail) => detail.value === true);

  return (
    <Stack gap={1}>
      {contact ? (
        <Typography variant="caption" color="text.secondary">
          {t("listingsDetails.lookingForListingsWith")}
        </Typography>
      ) : (
        <Typography variant="h5">{t("listingsDetails.moreAboutTheProperty")}</Typography>
      )}
      {shelter.length > 0 && (
        <Box
          sx={{
            p: 1.25,
            borderRadius: 1,
            border: 1,
            borderColor: shelterActive ? "primary.main" : "divider",
            bgcolor: (theme) => (shelterActive ? alpha(theme.palette.primary.main, 0.06) : "transparent")
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", display: "block", mb: 0.75 }}>
            {PROTECTED_SPACE_GROUP_LABEL}
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1.5}>
            {shelter.map(chipOf)}
          </Stack>
          {/* Only once something is ticked — most cards ask for no shelter at all, and an
              always-on sentence here is noise on every one of them. */}
          {shelterActive && (
            <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "primary.dark" }}>
              {PROTECTED_SPACE_GROUP_HINT}
            </Typography>
          )}
        </Box>
      )}
      <Stack direction="row" flexWrap="wrap" gap={2}>
        {rest.map(chipOf)}
      </Stack>
    </Stack>
  );
}

export function getListingAdditionalDetails({
  listing,
  showAddressDisplayMode
}: ListingAdditionalDetailsComponentProps): {
  title: string;
  value: string | number | Date;
  formattedValue: string | number;
}[] {
  const { t: translate } = useTranslation();
  const { currentLang } = useLocales();

  const {
    purposeType,
    entranceType,
    entranceDate,
    propertyType,
    sizeOfGardenM2,
    sizeOfTerraceM2,
    transactionType,
    parkingSpotsCount,
    propertyCondition,
    addressDisplayMode,
    buildingTotalFloors
  } = listing;

  let parsedFormattedEntranceDate: string | null = null;

  const shouldShowEntranceType = ["immediate", "flexible"].includes(entranceType);

  if (shouldShowEntranceType) {
    parsedFormattedEntranceDate = translate(`listingsPage.evictionTypeLabel.${entranceType}`);
  } else if (entranceDate instanceof Date) {
    parsedFormattedEntranceDate = entranceDate?.toLocaleDateString?.(currentLang.dateLocale);
  }

  return [
    {
      title: translate("listingsDetails.purposeType"),
      value: purposeType,
      formattedValue: purposeType
        ? translate<string>(`listingsPage.purposeTypeLabel.${purposeType}`)
        : null
    },
    {
      title: translate("listingsDetails.propertyType"),
      value: propertyType,
      formattedValue: propertyType
    },
    {
      title: translate("listingsDetails.transactionType"),
      value: transactionType,
      formattedValue: transactionType
        ? translate<string>(`listingsPage.transactionTypeLabel.${transactionType}`)
        : null
    },
    ...(showAddressDisplayMode
      ? [
          {
            title: translate("listingsDetails.addressDisplayMode"),
            value: addressDisplayMode,
            formattedValue: addressDisplayMode
              ? translate<string>(`listingsPage.addressDisplayModeLabel.${addressDisplayMode}`)
              : null
          }
        ]
      : []),
    {
      title: translate("listingsDetails.propertyCondition"),
      value: propertyCondition,
      formattedValue: propertyCondition
    },
    {
      title: translate("listingsDetails.buildingTotalFloors"),
      value: buildingTotalFloors,
      formattedValue: buildingTotalFloors
    },
    {
      title: translate("listingsDetails.parkingSpotsCount"),
      value: parkingSpotsCount,
      formattedValue: parkingSpotsCount
    },
    {
      title: translate("listingsDetails.entranceDate"),
      value: parsedFormattedEntranceDate,
      formattedValue: parsedFormattedEntranceDate
    },
    {
      title: translate("listingsDetails.sizeOfGardenM2"),
      value: sizeOfGardenM2,
      formattedValue: sizeOfGardenM2
        ? `${sizeOfGardenM2} ${translate("listingsMatch.sqrMeter")}`
        : null
    },
    {
      title: translate("listingsDetails.sizeOfTerraceM2"),
      value: sizeOfTerraceM2,
      formattedValue: sizeOfTerraceM2
        ? `${sizeOfTerraceM2} ${translate("listingsMatch.sqrMeter")}`
        : null
    }
  ];
}

type ListingAdditionalDetailsComponentProps = {
  listing: ProcessedListingData;
  showAddressDisplayMode?: boolean;
};

export function ListingAdditionalDetailsComponent({
  listing,
  showAddressDisplayMode
}: ListingAdditionalDetailsComponentProps): JSX.Element {
  const { t } = useTranslation();
  const additionalDetails = getListingAdditionalDetails({ listing, showAddressDisplayMode });

  if (!additionalDetails || additionalDetails.length === 0) {
    return null;
  }

  return (
    <Stack spacing={1}>
      <Typography variant="h5">{t("listingsDetails.moreDetails")}</Typography>

      <Stack direction={"column"} spacing={0.5}>
        {additionalDetails?.map((row) => {
          if (!row?.formattedValue) {
            return null;
          }

          return (
            <Stack direction={"row"} spacing={0.5} key={row.title}>
              <Box width={"150px"}>
                <Label color="primary">{row.title}</Label>
              </Box>
              <Typography variant="subtitle1">{row.formattedValue}</Typography>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
}
